import { action, makeObservable, observable } from 'mobx';
import utilsService from '../services/UtilsService';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export class DrawManager {
  constructor() {
    makeObservable(this);
  }

  @observable textContainers = {};
  toolbarUiByLessonElementId = {};

  dragProps = null;
  lessonElementId = null;

  getToolbarUi(lessonElementId) {
    return this.toolbarUiByLessonElementId[lessonElementId] || null;
  }

  setToolbarUi(lessonElementId, ui) {
    if (!lessonElementId) {
      return;
    }
    this.toolbarUiByLessonElementId[lessonElementId] = ui;
  }

  // prevent data for a previous slide from being used in the current slide
  @action init(lessonElementId) {
    if (this.lessonElementId !== lessonElementId) {
      this.lessonElementId = lessonElementId;
      if (!this.textContainers[lessonElementId]) {
        this.textContainers[lessonElementId] = [];
      }
      this.dragProps = null;
    }
  }

  @action addTextContainer(textContainer, lessonElementId) {
    this.textContainers[lessonElementId].push(textContainer);
  }

  @action initializeTextContainers(textModels, lessonElementId) {
    const textContainers = [];
    this.textContainers[lessonElementId] = [];

    if (!textModels) {
      return;
    }

    textModels.forEach((textModel) => {
      const textContainer = {
        textId: textModel.id,
        top: textModel.position.top,
        left: textModel.position.left,
        text: textModel.text,
        lessonElementId,
        opacity: 1,
      };
      textContainers.push(textContainer);
    });

    this.textContainers[lessonElementId] = textContainers;
  }

  @action setTextContainerText(textId, data, lessonElementId) {
    const textContainer = this.textContainers[lessonElementId].find((container) => container.textId === textId);
    textContainer.text = data;
  }

  findTextContainer(textId, lessonElementId) {
    const textContainer = this.textContainers[lessonElementId].find((container) => container.textId === textId);
    return textContainer;
  }

  @action deleteTextContainer(textId, lessonElementId) {
    const newTextContainers = this.textContainers[lessonElementId].filter((container) => container.textId !== textId);
    this.textContainers[lessonElementId] = newTextContainers;
  }

  @action dragStartTextContainer(textId, clientX, clientY, width, height, lessonElementId) {
    const textContainer = this.findTextContainer(textId, lessonElementId);
    if (!textContainer) {
      return;
    }
    this.dragProps = {
      textId,
      clientX,
      clientY,
      originLeft: Number(textContainer.left) || 0,
      originTop: Number(textContainer.top) || 0,
      width,
      height,
      lessonElementId,
    };
    textContainer.opacity = 0.5;
  }

  @action moveTextContainer(clientX, clientY, maxWidth = 600, maxHeight = 600) {
    if (!this.dragProps) {
      return;
    }
    const textContainer = this.findTextContainer(this.dragProps.textId, this.dragProps.lessonElementId);
    if (!textContainer) {
      return;
    }

    const maxLeft = Math.max(0, maxWidth - this.dragProps.width);
    const maxTop = Math.max(0, maxHeight - this.dragProps.height);
    textContainer.left = clamp(
      this.dragProps.originLeft + (clientX - this.dragProps.clientX),
      0,
      maxLeft
    );
    textContainer.top = clamp(
      this.dragProps.originTop + (clientY - this.dragProps.clientY),
      0,
      maxTop
    );
  }

  @action dropTextContainer(clientX, clientY, lessonElementId, maxWidth = 600, maxHeight = 600) {
    if (!this.dragProps) {
      return;
    }
    if (typeof clientX === 'number' && typeof clientY === 'number') {
      this.moveTextContainer(clientX, clientY, maxWidth, maxHeight);
    }
    const textContainer = this.findTextContainer(
      this.dragProps.textId,
      lessonElementId || this.dragProps.lessonElementId
    );
    if (textContainer) {
      textContainer.opacity = 1;
    }
    this.dragProps = null;
  }
}

export default new DrawManager();
